import type { ReactNode } from "react";
import { AlertCircle, ArrowLeft, Clock } from "lucide-react";
import { Button } from "../../components/button";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../components/card";
import {
    configOrderIndex,
    formatConfigValue,
} from "../../lib/productConfigFormat";
// import { UNIVDIAM_LOGO } from "../../lib/univdiamLogo";
import type { ProductImage } from "../../type";
import { ConfigRow } from "./ConfigRow";
import { OptionButton } from "./OptionButton";
import ProductGallery from "./ProductGallery";

export type ShareConfigItem = {
    /** Row label (already humanized, e.g. "Metal Type"). */
    label: string;
    /** Displayed value + button text. */
    value: string;
    /** Optional custom control; defaults to a read-only selected OptionButton. */
    render?: ReactNode;
};

export type ShareCustomField = {
    id: string;
    label: string;
    value: string;
};

export type ProductShareViewProps = {
    images: ProductImage[];
    iframeLink?: string;
    title?: string;
    /** Pre-computed price to display. Rendered as `$ {price}` when provided. */
    price?: number | string;
    config: ShareConfigItem[];
    instructions?: string;
    customFields?: ShareCustomField[];
    /** Extra rows appended after config (e.g. a plain Quantity row). */
    footerRows?: ReactNode;

    /** When set, renders a "Product View" back bar above the content. */
    onBack?: () => void;
    /** Label shown next to the back arrow. */
    backLabel?: string;

    /** Async state. When set, view renders loading / error cards instead. */
    isLoading?: boolean;
    isError?: boolean;
    /** HTTP status used to pick the error copy (410 = expired, 401 = invalid). */
    errorStatus?: number;
    onRetry?: () => void;

    /** Layout overrides. */
    galleryClassName?: string;
    dataClassName?: string;
    productID?: string
};

const LoadingState = () => (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
            <p className="text-gray-500">Loading product...</p>
        </div>
    </div>
);

const ErrorState = ({
    status,
    onRetry,
}: {
    status?: number;
    onRetry?: () => void;
}) => {
    const expired = status === 410;
    const message = expired
        ? "This share link has expired."
        : status === 401
            ? "This share link is invalid."
            : "This share link could not be found.";

    return (
        <div className="flex items-center justify-center min-h-[100dvh] bg-slate-50/50 p-4">
            <Card className="max-w-md w-full border-zinc-200 mt-[-10%]">
                <CardHeader className="text-center pb-2">
                    <div className="flex justify-center mb-4">
                        {expired ? (
                            <div className="p-3 bg-amber-50 rounded-full">
                                <Clock className="h-10 w-10 text-amber-600" />
                            </div>
                        ) : (
                            <div className="p-3 bg-red-50 rounded-full">
                                <AlertCircle className="h-10 w-10 text-red-600" />
                            </div>
                        )}
                    </div>
                    <CardTitle className="text-2xl font-bold tracking-tight">
                        {expired ? "Link Expired" : "Invalid Link"}
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                    <p className="text-muted-foreground leading-relaxed">{message}</p>
                </CardContent>
                <CardFooter className="flex flex-col gap-2 pt-4">
                    <Button
                        className="w-full"
                        variant="default"
                        onClick={onRetry ?? (() => window.location.reload())}
                    >
                        Try again
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

/**
 * Read-only product summary: gallery + description + configuration.
 * Powers every share / submitted-order view (CAD, catalog, admin, customer).
 * UI layout matches ProductDetail.tsx.
 */
const ProductShareView = ({
    images,
    iframeLink,
    title,
    price,
    config,
    instructions,
    customFields,
    footerRows,
    onBack,
    backLabel = "Product View",
    isLoading,
    isError,
    errorStatus,
    onRetry,
    galleryClassName = "p-4 bg-white w-full rounded-md border border-gray-200 md:h-full md:min-h-0 md:overflow-hidden",
    dataClassName = "flex flex-col gap-4 w-full",
    productID
}: ProductShareViewProps) => {
    if (isLoading) return <LoadingState />;
    if (isError) return <ErrorState status={errorStatus} onRetry={onRetry} />;

    // Metal Type -> Diamond Quality -> Carat Weight, then anything else in the
    // order the caller passed it. Values get the "ct" suffix / LG -> Lab Grown
    // expansion here so every caller renders them the same way.
    const orderedConfig = config
        .map((item, index) => ({ item, index }))
        .sort(
            (a, b) =>
                configOrderIndex(a.item.label) - configOrderIndex(b.item.label) ||
                a.index - b.index
        )
        .map(({ item }) => ({
            ...item,
            value: formatConfigValue(item.label, item.value),
        }));

    return (
        <div className="p-[20px] md:h-screen md:flex md:flex-col md:overflow-hidden">
            {onBack && (
                <div className="border rounded-[4px] p-3 transition-colors mb-2 md:mb-3 bg-white flex items-center gap-2">
                    <ArrowLeft
                        size={20}
                        strokeWidth={1}
                        onClick={onBack}
                        className="cursor-pointer"
                    />
                    <p className="font-semibold">{backLabel}</p>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-4 w-full md:flex-1 md:min-h-0">
                <div className={galleryClassName}>
                    <ProductGallery images={images} iFrameLink={iframeLink} />
                </div>

                <div id="data-col" className={`${dataClassName} md:h-full md:min-h-0 md:overflow-y-auto`}>
                    {/* Brand */}
                    {/* <img
                        src={UNIVDIAM_LOGO}
                        alt="Univdiam"
                        className="h-10 w-auto object-contain self-start mb-2"
                    /> */}

                    {/* Title + Price Card */}
                    <div id="title-card" className="bg-white rounded-md border border-gray-200">
                        <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                            <h1 className="text-body1">Product Description</h1>
                        </header>
                        {(productID || title || (price !== undefined && price !== null)) && (
                            <div className="px-4 py-5 flex flex-col gap-3">
                                {productID && (
                                    <p className="text-sm font-medium text-muted-foreground">{productID}</p>
                                )}
                                {title && <h2 className="text-[18px]">{title}</h2>}
                                {price !== undefined && price !== null && (
                                    <p className="text-[18px]">$ {Number(price).toLocaleString('en-US')}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Configuration Card */}
                    {orderedConfig.length > 0 && (
                        <div className="bg-white rounded-md border border-gray-200">
                            <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                                <h1 className="text-body1">Product Configuration</h1>
                            </header>
                            <div className="px-4 flex flex-col gap-8 py-6">
                                {orderedConfig.map((item) => (
                                    <ConfigRow
                                        key={item.label}
                                        label={item.label}
                                        value={item.value}
                                    >
                                        {item.render ?? (
                                            <OptionButton
                                                label={item.value}
                                                isSelected={true}
                                                onClick={() => { }}
                                            />
                                        )}
                                    </ConfigRow>
                                ))}
                                {footerRows}
                            </div>
                        </div>
                    )}

                    {/* Additional custom fields Card */}
                    {Array.isArray(customFields) && customFields.length > 0 && (
                        <div className="bg-white rounded-md border border-gray-200">
                            <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                                <h1 className="text-body1">Additional Fields</h1>
                            </header>
                            <div className="px-4 py-4 divide-y">
                                {customFields.map((field) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-[40%_60%] text-sm py-2"
                                    >
                                        <span className="text-muted-foreground">
                                            {field.label}
                                        </span>
                                        <span className="font-medium">{field.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Instructions Card */}
                    {instructions && (
                        <div className="bg-white rounded-md border border-gray-200">
                            <header className="flex items-center space-x-4 p-4 border-b border-gray-200">
                                <h1 className="text-body1">Instructions</h1>
                            </header>
                            <div className="px-4 flex flex-col gap-8 py-6">
                                <p>{instructions}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductShareView;
